import React from 'react';
import { Sparkles, Lock, Download } from 'lucide-react';
import { createGlassCard, createButton, sharedStyles } from './shared/sharedStyles.js';
import ShowMatchCard from './ShowMatchCard.jsx';

/**
 * ShowMatchTable - Table container for show recommendations
 *
 * GUEST-SIDE mirror of GuestReportTable.jsx. Displays show
 * recommendations in a table format with export functionality.
 */
const ShowMatchTable = ({
  showRecommendations,
  copiedIndex,
  onCopyEmail,
  onViewPitch,
  hasPremiumAccess = false,
  guestName = ''
}) => {
  const handleExportCSV = () => {
    // Prepare CSV headers (using snake_case for data hygiene)
    const headers = [
      'show_name',
      'host_name',
      'network',
      'relevance_score',
      'why_good_fit',
      'topics_covered',
      'audience_size',
      'accessibility_score',
      'report_date'
    ];

    // Add premium fields if user has access
    if (hasPremiumAccess) {
      headers.push('contact_email', 'website', 'pitch_form_url', 'pitch');
    }

    // Helper function to escape CSV values
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Convert show data to CSV rows
    const rows = showRecommendations.map(show => {
      const discoveredDate = show.discoveredAt
        ? new Date(show.discoveredAt).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short'
        })
        : '';

      const row = [
        escapeCSV(show.showName),
        escapeCSV(show.hostName),
        escapeCSV(show.network),
        escapeCSV((show.relevanceScore * 10).toFixed(1)),
        escapeCSV(show.whyGoodFit),
        escapeCSV(show.topicsCovered?.join('; ') || ''),
        escapeCSV(show.audienceSize),
        escapeCSV(show.accessibilityScore ? (show.accessibilityScore * 10).toFixed(1) : ''),
        escapeCSV(discoveredDate)
      ];

      // Add premium fields if user has access
      if (hasPremiumAccess) {
        row.push(
          escapeCSV(show.contactEmail || ''),
          escapeCSV(show.website || ''),
          escapeCSV(show.pitchFormUrl || ''),
          escapeCSV(show.pitch || '')
        );
      }

      return row.join(',');
    });

    // Combine headers and rows
    const csv = [headers.join(','), ...rows].join('\n');

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Generate filename with date
    const date = new Date().toISOString().split('T')[0];
    const filename = `show_recommendations_${date}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{
      flex: 1,
      ...createGlassCard(),
      display: 'flex',
      flexDirection: 'column',
      minHeight: 'fit-content'
    }}>
      {/* Panel Header */}
      <div style={{
        padding: '1rem 1.25rem',
        borderBottom: `1px solid ${sharedStyles.colors.primary}20`,
        background: sharedStyles.colors.surfaceAlt
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h2 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: sharedStyles.colors.textPrimary,
              margin: 0
            }}>
              Show Recommendations
            </h2>
            <Sparkles size={18} style={{ color: sharedStyles.colors.primary }} />
            <span style={{
              color: sharedStyles.colors.textSecondary,
              fontSize: '0.8rem',
              marginLeft: '0.5rem'
            }}>
              AI-matched shows based on your profile
            </span>
          </div>

          {/* Export CSV Button */}
          <button
            onClick={handleExportCSV}
            style={{
              ...createButton('outline'),
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Show Table */}
      <div style={{
        flex: 1,
        overflowY: 'auto'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '22% 8% 1fr 12% 10%',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid rgba(234, 88, 12, 0.1)',
          background: 'rgba(234, 88, 12, 0.02)',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: sharedStyles.colors.textSecondary,
            textTransform: 'uppercase'
          }}>
            Show
          </div>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: sharedStyles.colors.textSecondary,
            textTransform: 'uppercase',
            textAlign: 'center'
          }}>
            Match
          </div>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: sharedStyles.colors.textSecondary,
            textTransform: 'uppercase'
          }}>
            Why Good Fit
          </div>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: hasPremiumAccess ? sharedStyles.colors.textSecondary : sharedStyles.colors.textTertiary,
            textTransform: 'uppercase',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.25rem'
          }}>
            {!hasPremiumAccess && <Lock size={10} />}
            Contact
          </div>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: hasPremiumAccess ? sharedStyles.colors.textSecondary : sharedStyles.colors.textTertiary,
            textTransform: 'uppercase',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.25rem'
          }}>
            {!hasPremiumAccess && <Lock size={10} />}
            Pitch
          </div>
        </div>

        {/* Show Rows */}
        {showRecommendations.map((show, index) => (
          <ShowMatchCard
            key={show.id}
            show={show}
            index={index}
            copiedIndex={copiedIndex}
            onCopyEmail={onCopyEmail}
            onViewPitch={onViewPitch}
            hasPremiumAccess={hasPremiumAccess}
          />
        ))}
      </div>
    </div>
  );
};

export default ShowMatchTable;
